import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import {
  buildNearMePreferencesFromAdminDefaults,
  type NearMeUserDefaultsPolicy,
} from "@/lib/notifications/near-me-user-defaults-policy";
import {
  isPhoneValidForNearMeWhatsapp,
  nextNearMeWhatsappConsentFields,
  normalizeProfilePhoneDigits,
  type NearMeWhatsappConsentSnapshot,
} from "@/lib/notifications/near-me-whatsapp-preference-sync";

export type HomepageNearMeProfileLocation = {
  location?: string | null;
  location_state_id?: string | null;
  location_lga_id?: string | null;
  location_neighborhood_id?: string | null;
};

export type HomepageNearMeQuickOptInRow = {
  user_id: string;
  notify_new_listings_near_me: true;
  new_listings_near_me_granularity: NonNullable<
    ReturnType<
      typeof buildNearMePreferencesFromAdminDefaults
    >["new_listings_near_me_granularity"]
  >;
  near_me_delivery_mode: "realtime" | "digest_daily";
  near_me_categories: null;
  near_me_subcategories: null;
  near_me_notify_email: true;
  near_me_notify_whatsapp: boolean;
  near_me_whatsapp_opt_in_at: string | null;
  near_me_whatsapp_opt_out_at: string | null;
  updated_at: string;
};

export type HomepageNearMeQuickOptInInput = {
  userId: string;
  nowIso: string;
  notifyWhatsapp: boolean;
  phoneDigits: string | null;
  previousWhatsapp: NearMeWhatsappConsentSnapshot;
  nearMePolicy?: NearMeUserDefaultsPolicy;
};

export type HomepageNearMeQuickOptInBuildResult =
  | { ok: true; row: HomepageNearMeQuickOptInRow }
  | { ok: false; code: "whatsapp_phone_invalid" };

/** True when the profile has a location string or structured location ids for matching. */
export function profileLocationUsableForNearMe(
  profile: HomepageNearMeProfileLocation,
): boolean {
  if (profile.location?.trim()) return true;
  return Boolean(
    profile.location_state_id ||
      profile.location_lga_id ||
      profile.location_neighborhood_id,
  );
}

/**
 * Admin-policy defaults for homepage quick opt-in; WhatsApp optional when on.
 */
export function buildHomepageNearMeQuickOptInRow(
  input: HomepageNearMeQuickOptInInput,
): HomepageNearMeQuickOptInBuildResult {
  const phoneOk = isPhoneValidForNearMeWhatsapp(input.phoneDigits);
  if (input.notifyWhatsapp && !phoneOk) {
    return { ok: false, code: "whatsapp_phone_invalid" };
  }

  const waNext = nextNearMeWhatsappConsentFields({
    nowIso: input.nowIso,
    previous: input.previousWhatsapp,
    requestedEnabled: input.notifyWhatsapp && phoneOk,
    phoneDigits: input.phoneDigits,
  });

  const nearMeFields = buildNearMePreferencesFromAdminDefaults(
    input.nearMePolicy,
    { nearMeEnabled: true },
  );
  const granularity = nearMeFields.new_listings_near_me_granularity!;
  const deliveryMode =
    nearMeFields.near_me_delivery_mode === "digest_daily"
      ? "digest_daily"
      : "realtime";

  return {
    ok: true,
    row: {
      user_id: input.userId,
      notify_new_listings_near_me: true,
      new_listings_near_me_granularity: granularity,
      near_me_delivery_mode: deliveryMode,
      near_me_categories: null,
      near_me_subcategories: null,
      near_me_notify_email: true,
      near_me_notify_whatsapp: waNext.near_me_notify_whatsapp,
      near_me_whatsapp_opt_in_at: waNext.near_me_whatsapp_opt_in_at,
      near_me_whatsapp_opt_out_at: waNext.near_me_whatsapp_opt_out_at,
      updated_at: input.nowIso,
    },
  };
}

export async function applyHomepageNearMeQuickOptIn(
  client: SupabaseClient<Database>,
  input: HomepageNearMeQuickOptInInput,
): Promise<
  | { ok: true; row: HomepageNearMeQuickOptInRow }
  | { ok: false; code: "whatsapp_phone_invalid" | "save_failed" }
> {
  const built = buildHomepageNearMeQuickOptInRow(input);
  if (!built.ok) return built;

  const { error } = await client
    .from("notification_preferences")
    .upsert(built.row, { onConflict: "user_id" });

  if (error) {
    console.error("[homepage_near_me] notification_preferences upsert:", error);
    return { ok: false, code: "save_failed" };
  }

  return { ok: true, row: built.row };
}

export function phoneDigitsFromProfilePhone(
  phone: string | null | undefined,
): string | null {
  return normalizeProfilePhoneDigits(phone ?? null);
}
